import { createPortal } from 'react-dom';
import { FILL_COPY } from './copy';
import type { DutyMonthsState } from './useDutyMonths';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §11b] ONE OFFICER'S RECENT PAs, MONTH BY MONTH, ON HOVER.
 *
 *  The search sheet showed a bare number and nothing said what it counted or over what. This is
 *  the breakdown: this month, last month and the month before.
 *
 *  ⚠️⚠️ ONE CARD FOR THE WHOLE SHEET — `OfficerHoverCard`'s shape and its measured reason.
 *  Divergence #82: an AntD `Tooltip` per ALLOCATION ROW took one suite from 64.25s to 119.18s,
 *  "each mounts an rc-trigger and a `ResizeObserver`". A search sheet renders up to thirty rows
 *  and re-renders them on every keystroke, so one per ROW is that cost multiplied and paid
 *  again on each letter typed. The sheet owns the hovered officer and renders this once.
 *
 *  ⚠️ IT IS NOT HOVER-ONLY, AND THE KEYBOARD HALF IS NOT A DOM FOCUS EVENT. The sheet's rows are
 *  `<li role="option">` under an `aria-activedescendant` combobox — focus never leaves the input
 *  by design, because that is what keeps the twelve-name click budget — so [PRE-P6.1]'s "opens
 *  on FOCUS as well as hover" cannot be satisfied with `onFocus` here. What "focus" means in
 *  this listbox is the arrow-key CURSOR landing on a row, and `OfficerSearchSheet` opens the
 *  card on that as well as on `onMouseEnter`. Without it the counts would be unreachable from
 *  the keyboard, which is [D4.3]'s argument against putting a real affordance behind a hover.
 *
 *  ⚠️ `position: fixed` AND A PORTAL, so the sheet's own scroll container cannot clip it, and
 *  clamped to the viewport on both axes. Placed from the row's `getBoundingClientRect()`, which
 *  jsdom reports as all-zero, so the RECTANGLE is a browser assertion (`e2e/specs/fillGaps.spec.ts`,
 *  [C5b §8d]). ⚠️ [amended C5b review] "The CONTENT is a unit assertion" was written here and no
 *  unit test pins this card's three states (loading, failed, months) — a comment claiming a guard
 *  nobody has. Reported, not written; CLAUDE.md §8 records it.
 *
 *  ⚠️ AND IT IS `pointer-events-none`. The card is placed under the pointer's own path, and one
 *  that could take the hover would flicker itself in and out.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** One card, so one id. `aria-describedby` on the active row points here. */
export const RECENT_PAS_CARD_ID = 'recent-pas-card';

/** A heading and three rows. */
const ESTIMATED_HEIGHT = 96;
const GAP = 8;
const MARGIN = 8;
const WIDTH = 220;

/**
 * ⚠️ `YYYY-MM` -> `September 2026`, IN IST AND WITHOUT LUXON.
 *
 * The month is already an IST business-date prefix (`assignments.duty_date`, [P3a]), so there is
 * no instant to convert and no zone to get wrong — the two numbers are read straight out of the
 * string. Building a `Date` from it would introduce exactly the UTC crossing [IST-FIX] banned.
 */
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/* ⚠️ NOT EXPORTED. `react-refresh/only-export-components` refuses a function export beside a
   component, and this has one caller — the card below. `OFFICER_HOVER_CARD_ID` is a const and is
   exempt; a function is not. */
const monthLabel = (month: string): string => {
  const [year, index] = month.split('-');
  const name = MONTH_NAMES[Number(index) - 1];
  return name === undefined || year === undefined ? month : `${name} ${year}`;
};

export type RecentPasCardProps = {
  state: DutyMonthsState;
  /** The row's rectangle, in viewport coordinates. */
  anchor: { top: number; left: number; width: number; height: number };
};

export const RecentPasCard = ({ state, anchor }: RecentPasCardProps) => {
  const below = anchor.top + anchor.height + GAP;
  const flips = below + ESTIMATED_HEIGHT + MARGIN > window.innerHeight;
  const top = flips ? Math.max(MARGIN, anchor.top - ESTIMATED_HEIGHT - GAP) : below;
  const left = Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - WIDTH - MARGIN));

  return createPortal(
    <div
      id={RECENT_PAS_CARD_ID}
      role="tooltip"
      data-testid="recent-pas-card"
      className="pointer-events-none fixed z-50 rounded-md border border-border bg-popover p-2.5 text-popover-foreground shadow-md"
      style={{ top, left, width: WIDTH }}
    >
      <p className="text-xs font-semibold">{FILL_COPY.recentPasTitle}</p>
      {/*
        ⚠️ THREE STATES, NOT TWO. A count that has not arrived and one that could not be fetched
        are different facts — `OfficerResultRow`'s own rule for the bare number it sits beside.
      */}
      {state.isPending ? (
        <p className="text-xs text-muted-foreground">{FILL_COPY.recentPasLoading}</p>
      ) : state.buckets === null ? (
        <p className="text-xs text-muted-foreground">{FILL_COPY.recentPasFailed}</p>
      ) : (
        <ul className="m-0 mt-1 space-y-0.5">
          {state.buckets.map((bucket) => (
            <li key={bucket.month} className="flex items-baseline justify-between gap-3 text-xs">
              <span className="text-muted-foreground">{monthLabel(bucket.month)}</span>
              <span className="font-medium tabular-nums">{bucket.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body,
  );
};
